import { SalesTableData } from "../interface";
import stringToNumber from "src/utils/stringToNumber";
import { formatToCurrency, getComponentKey } from "src/utils/genericFunctions";

export const modifyData = (
  data: SalesTableData[],
  page: number,
  rowsPerPage: number
) => {
  const modifiedData: any = [...data];

  const sumInitialState = {
    totalUnits: 0,
    totalCases: 0,
    totalCasesSold: 0,
    totalCasesReceived: 0,
    totalCredits: 0,
    extendedRetail: 0
  };

  let sum = { ...sumInitialState };

  const addToSum = (rowData: any) => {
    sum.totalUnits += rowData?.totalUnits
      ? stringToNumber(rowData?.totalUnits)
      : 0;
    sum.totalCases += rowData?.totalCases
      ? stringToNumber(rowData?.totalCases)
      : 0;
    sum.totalCasesSold += rowData?.totalCasesSold
      ? stringToNumber(rowData?.totalCasesSold)
      : 0;
    sum.totalCasesReceived += rowData?.totalCasesReceived
      ? stringToNumber(rowData?.totalCasesReceived)
      : 0;
    sum.totalCredits += rowData?.totalCredits
      ? stringToNumber(rowData?.totalCredits)
      : 0;
    sum.extendedRetail += rowData?.extendedRetail
      ? stringToNumber(rowData?.extendedRetail)
      : 0;
  };

  const months = [
    "jan",
    "feb",
    "mar",
    "apr",
    "may",
    "jun",
    "jul",
    "aug",
    "sep",
    "oct",
    "nov",
    "dec"
  ];

  const averager = (index: number, weekEnded: string) => {
    const totalUnits = sum.totalUnits.toFixed(2);
    const totalCases = sum.totalCases.toFixed(2);
    const totalCasesSold = sum.totalCasesSold.toFixed(2);
    const totalCasesReceived = sum.totalCasesReceived.toFixed(2);
    const totalCredits = formatToCurrency(sum.totalCredits);
    const extendedRetail = formatToCurrency(sum.extendedRetail);
    const month = months[Number(weekEnded.split("/")[0]) - 1];
    const year = Number(weekEnded.split("/")[2]);
    const avgRetail = formatToCurrency(sum.extendedRetail / sum.totalUnits);

    return {
      data: {
        weekEnded: `${month} ${year}`,
        totalCases,
        totalUnits,
        totalCasesSold,
        totalCasesReceived,
        totalCredits,
        avgRetail,
        extendedRetail,
        isRowHighlighted: true,
        isCalculatedDataRow: true,
        key: getComponentKey()
      },
      index
    };
  };

  const resetSum = () => (sum = { ...sumInitialState });

  const averageData: any = [];

  let lastIndex = 0;
  data.forEach((rowData, index) => {
    if (index === 0) {
      addToSum(rowData); // seed the sum with this data
    } else {
      const prevRowData = data[index - 1];
      if (
        prevRowData?.weekEnded?.split("/")[0] ===
        rowData?.weekEnded?.split("/")[0] // Compare months
      ) {
        addToSum(rowData); // Add that data to the sum
      } else {
        // Month changed
        averageData.push(averager(lastIndex, prevRowData.weekEnded));
        resetSum();
        addToSum(rowData); // Reset the sum with this data
        lastIndex = index;
      }
    }
    if (index === data.length - 1) {
      // there is no month change for the last row. Add it manually
      averageData.push(averager(lastIndex, data[index].weekEnded));
    }
  });

  // Insert average data in between the actual data
  averageData.forEach((item: any, loopIndex: number) => {
    const { data, index } = item;
    modifiedData.splice(index + loopIndex, 0, data);
  });
  const consolidatedIndexes = [] as any;
  modifiedData?.forEach((item: any, index: number) => {
    if (item?.isRowHighlighted) {
      consolidatedIndexes.push(index);
    }
  });
  const paginatedData = getPaginatedData(
    modifiedData,
    consolidatedIndexes,
    page,
    rowsPerPage
  );
  return { modifiedData: paginatedData, averageData };
};

export const getPaginatedData = (
  data: any,
  consolidatedIndexes: number[],
  page: number,
  rowsPerPage: number
) => {
  return data?.slice(
    consolidatedIndexes[(page - 1) * rowsPerPage],
    consolidatedIndexes[page * rowsPerPage]
      ? consolidatedIndexes[page * rowsPerPage]
      : consolidatedIndexes[consolidatedIndexes?.length]
  );
};

export const sumWeeklyData = (weeklyData: any, item: any) => {
  return {
    ...weeklyData,
    totalUnits:
      stringToNumber(weeklyData.totalUnits || "0") +
      stringToNumber(item.totalUnits || "0"),
    totalCases: (
      stringToNumber(weeklyData.totalCases || "0") +
      stringToNumber(item.totalCases || "0")
    ).toFixed(2),
    totalCasesSold: (
      stringToNumber(weeklyData.totalCasesSold || "0") +
      stringToNumber(item.totalCasesSold || "0")
    ).toFixed(2),
    totalCasesReceived: (
      stringToNumber(weeklyData.totalCasesReceived || "0") +
      stringToNumber(item.totalCasesReceived || "0")
    ).toFixed(2),
    totalCredits: formatToCurrency(
      stringToNumber(weeklyData.totalCredits || "0") +
        stringToNumber(item.totalCredits || "0")
    ),
    extendedRetail: formatToCurrency(
      stringToNumber(weeklyData.extendedRetail || "0") +
        stringToNumber(item.extendedRetail || "0")
    )
  };
};

export const searchByDocId = (data: any[], searchTerm: string) => {
  return data.filter(
    (item: { docId: string; itemId: string }) =>
      item.docId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.itemId?.toLowerCase().includes(searchTerm.toLowerCase())
  );
};

export const getDataForUserRole = (data: any[], isApprover: boolean) => {
  if (isApprover) {
    return data;
  }

  return data
    ?.filter(
      (item: { totalUnits: string; totalCases: string }) =>
        stringToNumber(item.totalUnits) > 0 ||
        stringToNumber(item.totalCases) > 0
    )
    .map((item: any) => ({
      ...item,
      subItems: item.subItems?.filter(
        (subItem: any) =>
          stringToNumber(subItem.totalUnits) > 0 ||
          stringToNumber(subItem.totalCases) > 0
      )
    }));
};
